import axios from 'axios';
import { formatAuthHeader, getStoredToken, isTokenExpired } from '../utils/authUtils';
import setupTokenRefreshInterceptor from '../utils/tokenRefreshInterceptor';

const CONTENT_API_BASE_URL = '/api';

// Logo types based on SPARC spec
export interface Logo {
  id: number;
  logoKey?: string;
  displayName: string;
  url: string;
  width?: number;
  height?: number;
  fileSizeKb?: number;
  isDefault: boolean;
  displayOrder: number;
  updatedAt?: string;  // ISO timestamp for cache-busting
}

/**
 * Map the backend's snake_case default_logo payload (Brand.default_logo on
 * the brand/publisher endpoints) to the camelCase Logo interface.
 * @param raw - The raw default_logo object, or null/undefined
 * @returns Logo, or null when absent
 */
export const mapDefaultLogo = (raw: any): Logo | null => {
  if (!raw) return null;
  return {
    id: raw.id,
    logoKey: raw.logo_key,
    displayName: raw.display_name,
    url: raw.url,
    width: raw.width,
    height: raw.height,
    fileSizeKb: raw.file_size_kb,
    isDefault: raw.is_default,
    displayOrder: raw.display_order,
  };
};

/**
 * Add cache-busting parameter to logo URL to force browser refresh
 * Uses the logo's updatedAt timestamp if available, otherwise current timestamp
 * Accepts both camelCase (Logo) and snake_case (backend Brand.default_logo) formats
 * @param logo - Logo object with URL (either format)
 * @returns URL with cache-busting parameter
 */
export const getLogoUrlWithCacheBusting = (logo: Logo | any): string => {
  const url = logo.url;

  // Support both camelCase (updatedAt) and snake_case (updated_at) formats
  const updatedAtValue = logo.updatedAt || logo.updated_at;

  // Use updatedAt timestamp if available (milliseconds since epoch)
  const timestamp = updatedAtValue
    ? new Date(updatedAtValue).getTime()
    : Date.now();

  // Add timestamp as query parameter
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}v=${timestamp}`;
};

// Create axios instance
const logoApiClient = axios.create({
  baseURL: CONTENT_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000,
});

// Shared refresh interceptor: attaches the current token (refreshing
// proactively near expiry) and heals 401s via refresh-once-and-retry. The
// old local interceptor silently DROPPED the header whenever the token was
// expired — logo calls then ran anonymous instead of refreshing.
setupTokenRefreshInterceptor(logoApiClient);

// In-memory cache for logo requests to prevent duplicate API calls
// Key: brandId, Value: { logos: Logo[], timestamp: number }
const logoCache = new Map<number, { logos: Logo[], timestamp: number }>();
const CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes

// Inflight requests map to prevent duplicate concurrent requests for the same brand
const inflightRequests = new Map<number, Promise<Logo[]>>();

/**
 * Invalidate cache for a specific brand
 * @param publisherId - Brand ID to invalidate cache for
 */
export const invalidateLogoCache = (publisherId: number): void => {
  logoCache.delete(publisherId);
};

/**
 * Map snake_case backend response to camelCase frontend format
 */
const mapLogoResponse = (backendLogo: any): Logo => ({
  id: backendLogo.id,
  logoKey: backendLogo.logo_key,
  displayName: backendLogo.display_name,
  url: backendLogo.url,
  width: backendLogo.width,
  height: backendLogo.height,
  fileSizeKb: backendLogo.file_size_kb,
  isDefault: backendLogo.is_default,
  displayOrder: backendLogo.display_order,
  updatedAt: backendLogo.updated_at,
});

/**
 * Get all logos for a brand
 * @param publisherId - Brand ID (parameter name kept for backwards compatibility)
 * @returns List of logos
 */
export const getPublisherLogos = async (publisherId: number): Promise<Logo[]> => {
  // Check cache first
  const cached = logoCache.get(publisherId);
  if (cached && (Date.now() - cached.timestamp < CACHE_DURATION_MS)) {
    return cached.logos;
  }

  // Check if there's already an inflight request for this brand
  const inflight = inflightRequests.get(publisherId);
  if (inflight) {
    return inflight;
  }

  // Create new request promise
  const requestPromise = (async () => {
    try {
      const response = await logoApiClient.get<{logos: any[], total: number}>(`/brands/${publisherId}/logos`);
      const logos = response.data.logos.map(mapLogoResponse);

      // Cache the result
      logoCache.set(publisherId, { logos, timestamp: Date.now() });

      // Remove from inflight
      inflightRequests.delete(publisherId);

      return logos;
    } catch (error) {
      // Remove from inflight on error
      inflightRequests.delete(publisherId);

      // Handle 401 gracefully - user may not be authenticated
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        console.warn(`[logoApi] Not authenticated to fetch logos for publisher ${publisherId}`);
        return [];
      }

      // Log other errors but return empty array instead of throwing
      // This prevents logo loading failures from breaking the entire page
      if (axios.isAxiosError(error) && error.response?.data?.detail) {
        console.error(`[logoApi] Failed to fetch logos: ${error.response.data.detail}`);
      } else {
        console.error('[logoApi] Failed to fetch logos:', error);
      }
      return [];
    }
  })();

  // Store the promise
  inflightRequests.set(publisherId, requestPromise);

  return requestPromise;
};

/**
 * Get the default logo for a brand
 * @param publisherId - Brand ID (parameter name kept for backwards compatibility)
 * @returns Default logo
 */
export const getDefaultLogo = async (publisherId: number): Promise<Logo | null> => {
  try {
    const response = await logoApiClient.get<{logo: any | null}>(`/brands/${publisherId}/default-logo`);

    // Backend returns {"logo": null} when no default is set
    if (!response.data.logo) {
      return null;
    }

    return mapLogoResponse(response.data.logo);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 404) {
        return null; // No default logo set
      }
      if (error.response?.data?.detail) {
        throw new Error(error.response.data.detail);
      }
    }
    throw error;
  }
};

/**
 * Set the default logo for a brand (admin only)
 * @param publisherId - Brand ID (parameter name kept for backwards compatibility)
 * @param logoId - Logo ID to set as default
 * @returns Success response
 */
export const setDefaultLogo = async (
  publisherId: number,
  logoId: number
): Promise<{ success: boolean; defaultLogoId: number }> => {
  try {
    const response = await logoApiClient.put<{ success: boolean; defaultLogoId: number }>(
      `/brands/${publisherId}/default-logo`,
      { logo_id: logoId }  // Backend expects snake_case
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.detail) {
      throw new Error(error.response.data.detail);
    }
    throw error;
  }
};

/**
 * Upload a new logo variant
 * @param publisherId - Brand ID (parameter name kept for backwards compatibility)
 * @param file - Image file
 * @param displayName - Display name for the logo
 * @param logoKey - Unique key for the logo
 * @returns Created logo
 */
export const uploadLogo = async (
  publisherId: number,
  file: File,
  displayName: string,
  logoKey: string
): Promise<Logo> => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('display_name', displayName);
    formData.append('logo_key', logoKey);

    const token = getStoredToken();
    if (!token) {
      throw new Error('No authentication token found. Please log in again.');
    }

    // Use logoApiClient to get token refresh benefits
    const response = await logoApiClient.post<any>(
      `/brands/${publisherId}/logos/upload`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 30000, // Longer timeout for file upload
      }
    );

    // Invalidate cache after successful upload so next fetch gets fresh data
    invalidateLogoCache(publisherId);

    return mapLogoResponse(response.data);
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.detail) {
      throw new Error(error.response.data.detail);
    }
    throw error;
  }
};

/**
 * Update logo metadata (display name and/or logo key)
 * @param logoId - Logo ID
 * @param updates - Updates to apply (displayName and/or logoKey)
 * @returns Updated logo
 */
export const updateLogoMetadata = async (
  logoId: number,
  updates: { displayName?: string; logoKey?: string }
): Promise<Logo> => {
  try {
    const requestBody: { display_name?: string; logo_key?: string } = {};

    if (updates.displayName) {
      requestBody.display_name = updates.displayName;
    }

    if (updates.logoKey) {
      requestBody.logo_key = updates.logoKey;
    }

    const response = await logoApiClient.put<any>(`/brands/logos/${logoId}`, requestBody);
    return mapLogoResponse(response.data);
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.detail) {
      throw new Error(error.response.data.detail);
    }
    throw error;
  }
};

/**
 * Update logo display name (legacy function for backwards compatibility)
 * @param logoId - Logo ID
 * @param displayName - New display name
 * @returns Updated logo
 */
export const updateLogoName = async (logoId: number, displayName: string): Promise<Logo> => {
  return updateLogoMetadata(logoId, { displayName });
};

/**
 * Delete a logo
 * @param logoId - Logo ID
 * @returns Success message
 */
export const deleteLogo = async (logoId: number): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await logoApiClient.delete<{ success: boolean; message: string }>(
      `/brands/logos/${logoId}`
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.detail) {
      throw new Error(error.response.data.detail);
    }
    throw error;
  }
};

/**
 * Set logo display mode for a brand
 * @param brandId - Brand ID
 * @param displayMode - Display mode ('icon_only', 'name_only', 'icon_and_name')
 * @returns Success response
 */
export const setLogoDisplayMode = async (
  brandId: number,
  displayMode: 'icon_only' | 'name_only' | 'icon_and_name'
): Promise<{ success: boolean; displayMode: string; message: string }> => {
  try {
    const response = await logoApiClient.put<{ success: boolean; display_mode: string; message: string }>(
      `/brands/${brandId}/display-mode`,
      { display_mode: displayMode }
    );
    return {
      success: response.data.success,
      displayMode: response.data.display_mode,
      message: response.data.message,
    };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data?.detail) {
      throw new Error(error.response.data.detail);
    }
    throw error;
  }
};

export default {
  getPublisherLogos,
  getDefaultLogo,
  setDefaultLogo,
  uploadLogo,
  updateLogoName,
  updateLogoMetadata,
  deleteLogo,
  setLogoDisplayMode,
  getLogoUrlWithCacheBusting,
};
